import { createSelector } from 'reselect';

import { NEW_STEP_KEYS, DEFAULT_STEP_PROPS, DEFAULT_FOLLOW_UP_STEP_PROPS } from 'constants/StepsConstants';
import { getIntegrations } from 'modules/app/selectors';
import { getSelectedDealerIntegrations } from 'selectors/dealerSelectors';

import { getProps, getRoot } from 'utils/selectors';

import { getData, getUI } from './general';

export const getAll = createSelector(getData, ({ entities }) => entities ?? {});

export const getById = createSelector(getAll, getProps, (entities, stepId) => entities[stepId]);

export const getSelectedStep = createSelector(getRoot, getProps, getById);

export const getByIds = createSelector(
  getAll,
  getProps,
  /** @param {string[]} ids */
  (entities, ids) => ids?.map(id => entities[id]).filter(step => !!step) ?? [],
);

export const isNew = createSelector(getProps, stepId => Object.keys(NEW_STEP_KEYS).includes(stepId));

export const getInitialValues = createSelector(isNew, getProps, getSelectedStep, (isNewStep, stepId, step) => {
  if (!isNewStep) {
    return step;
  }

  const isFollowUp = stepId === NEW_STEP_KEYS.newFollowUp;

  /** @type {Partial<import('modules/steps/types/step').Step>} */
  return {
    ...DEFAULT_STEP_PROPS,
    ...(isFollowUp ? DEFAULT_FOLLOW_UP_STEP_PROPS : {}),
  };
});

export const getStepsIntegrations = createSelector(
  getIntegrations,
  getSelectedDealerIntegrations,
  (integration, dealerIntegrations) =>
    Object.entries(integration)
      .filter(
        ([integrationName, { allowStep }]) =>
          allowStep &&
          dealerIntegrations.includes(
            /** @type {import('modules/global/typing/enums/integrations.enum').IntegrationTypeEnum} */ (
              integrationName
            ),
          ),
      )
      .map(([name, data]) => ({ name, ...data })),
);

export const isDetailReady = createSelector(getUI, ({ details }) => details.isReady);

export const isFollowUpConfigModalOpen = createSelector(getUI, ({ details }) => details.isFollowUpConfigModalOpen);

const getDetailsLoadingState = createSelector(getUI, ({ details }) => details.loading);

export const isSavingFollowUpConfig = createSelector(getDetailsLoadingState, loading => loading.isSavingFollowUpConfig);

export const isStepSaving = createSelector(getDetailsLoadingState, loading => loading.update || loading.create);
